/**
 * useVulnerabilityKeyboard Hook
 *
 * Manages keyboard shortcut handling for the vulnerabilities table including:
 * - Status change shortcuts (bare letters: T, O, C, A, R)
 * - POE mode toggle (`p`)
 * - Keyboard event registration/cleanup
 *
 * This hook encapsulates all keyboard-related logic extracted from
 * the main vulnerabilities component.
 */

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from 'react';

import { HOTKEY_SCOPES, useActionHotkey } from '@/hooks/hotkeys';
import {
  createConfirmationState,
  getStatusFromKeyboardCode,
  getVulnerabilityKeyboardConfig,
  isValidKeyboardAction,
  shouldShowDeleteModal,
} from '@/sections/vulnerabilities/utils/keyboardShortcuts.util';
import { useGlobalState } from '@/state/global.state';
import { Risk, RiskStatus } from '@/types';

/**
 * Confirmation state for keyboard-triggered actions
 */
export interface KeyboardConfirmationState {
  /** The status to change to */
  status: string;
  /** The risks to update */
  data: Risk[];
}

/**
 * Hook parameters
 */
export interface UseVulnerabilityKeyboardParams {
  /** Whether keyboard shortcuts are enabled */
  enabled?: boolean;
  /** Callback when keyboard action is triggered */
  onKeyboardAction?: (code: string, data: Risk[]) => void;
  /** Callback when delete modal should be shown */
  onShowDeleteModal?: (risks: Risk[]) => void;
  /** Returns the currently inspected risk (click/arrow-nav), or null if none. */
  getInspectedRisk?: () => Risk | null;
  /** Delegates the POE-enter open to the parent (which owns drawer/global state). */
  onEnterPoe?: (risk: Risk) => void;
  /** Gate for the `p`-enter binding; pass false for the embedded inDrawer list. */
  enablePoeEnter?: boolean;
}

/**
 * Hook return value
 */
export interface UseVulnerabilityKeyboardReturn {
  /** Keyboard action configuration for Table component */
  keyboardConfig: ReturnType<typeof getVulnerabilityKeyboardConfig>;
  /** Handle keyboard action from table */
  handleKeyboardAction: (code: string, data: Risk[]) => void;
  /** Current confirmation state (for modals) */
  confirmationState: KeyboardConfirmationState | null;
  /** Set confirmation state */
  setConfirmationState: Dispatch<
    SetStateAction<KeyboardConfirmationState | null>
  >;
  /** Whether delete modal should be shown */
  showDeleteModal: boolean;
  /** Set delete modal visibility */
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
  /** Clears confirmation state */
  clearConfirmation: () => void;
}

/**
 * Hook for managing vulnerability keyboard shortcuts.
 *
 * @param params - Hook configuration parameters
 * @returns Keyboard state and handler functions
 *
 * @example
 * ```tsx
 * const keyboard = useVulnerabilityKeyboard({
 *   enabled: true,
 *   onKeyboardAction: (code, data) => {
 *     console.log('Keyboard action:', code, data);
 *   },
 *   onShowDeleteModal: (risks) => {
 *     setRisksToDelete(risks);
 *   }
 * });
 *
 * // Use in Table component
 * <Table
 *   keyboardActions={keyboard.keyboardConfig}
 *   onKeyboardAction={keyboard.handleKeyboardAction}
 * />
 *
 * // Handle confirmation modal
 * {keyboard.confirmationState && (
 *   <ConfirmationModal
 *     status={keyboard.confirmationState.status}
 *     risks={keyboard.confirmationState.data}
 *     onConfirm={handleConfirm}
 *     onCancel={keyboard.clearConfirmation}
 *   />
 * )}
 * ```
 */
export function useVulnerabilityKeyboard({
  enabled = true,
  onKeyboardAction,
  onShowDeleteModal,
  getInspectedRisk,
  onEnterPoe,
  enablePoeEnter = true,
}: UseVulnerabilityKeyboardParams = {}): UseVulnerabilityKeyboardReturn {
  const { poeMode } = useGlobalState();
  const [confirmationState, setConfirmationState] =
    useState<KeyboardConfirmationState | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // `p` → enter POE mode (the ENTER half of the single `p` toggle). This is one
  // of two mutually-exclusive `p` handlers: this enter handler is enabled ONLY
  // while POE mode is off, and the matching EXIT handler (in POE.tsx) is enabled
  // ONLY while POE mode is on — so exactly one is ever armed and a single `p`
  // press can never double-fire. Replaces the old Alt+ArrowRight enter. Scope
  // `overlay` so it stays armed alongside the drawer. Reads the inspected risk
  // via getInspectedRisk and delegates the drawer open to the parent (which owns
  // drawer/global wiring); no-op when nothing is inspected.
  useActionHotkey(
    'p',
    () => {
      const risk = getInspectedRisk?.();
      if (!risk) return; // no-op when nothing inspected
      onEnterPoe?.(risk); // parent opens drawer on Evidence + arming handled by drawer effect
    },
    {
      enabled: enabled && !poeMode.enabled && enablePoeEnter,
      scope: HOTKEY_SCOPES.overlay,
    }
  );

  // Clear confirmation state
  const clearConfirmation = useCallback(() => {
    setConfirmationState(null);
    setShowDeleteModal(false);
  }, []);

  // Handle keyboard action from table
  const handleKeyboardAction = useCallback(
    (code: string, data: Risk[]) => {
      if (!enabled || !isValidKeyboardAction(code, data)) {
        return;
      }

      // Check if this should trigger delete modal
      if (shouldShowDeleteModal(code)) {
        setShowDeleteModal(true);
        if (onShowDeleteModal) {
          onShowDeleteModal(data);
        }
        return;
      }

      // Create confirmation state
      const state = createConfirmationState(code, data);
      if (state) {
        setConfirmationState(state);
      }

      // Call optional callback
      if (onKeyboardAction) {
        onKeyboardAction(code, data);
      }
    },
    [enabled, onKeyboardAction, onShowDeleteModal]
  );

  // Get keyboard configuration
  const keyboardConfig = getVulnerabilityKeyboardConfig();

  return {
    keyboardConfig,
    handleKeyboardAction,
    confirmationState,
    setConfirmationState,
    showDeleteModal,
    setShowDeleteModal,
    clearConfirmation,
  };
}

/**
 * Gets the status value from a keyboard code.
 *
 * @param code - The keyboard code (e.g., 'KeyT', 'KeyO')
 * @returns The corresponding RiskStatus or 'remediated' for delete
 *
 * @example
 * ```tsx
 * const status = getKeyboardStatus('KeyT'); // RiskStatus.Triaged
 * ```
 */
export function getKeyboardStatus(
  code: string
): RiskStatus | 'remediated' | undefined {
  return getStatusFromKeyboardCode(code);
}

/**
 * Checks if a keyboard code should trigger a confirmation modal.
 *
 * @param code - The keyboard code
 * @returns True if confirmation is needed
 *
 * @example
 * ```tsx
 * if (needsKeyboardConfirmation('KeyC')) {
 *   // Show confirmation modal
 * }
 * ```
 */
export function needsKeyboardConfirmation(code: string): boolean {
  const status = getStatusFromKeyboardCode(code);
  return status !== undefined;
}

/**
 * Checks if a keyboard code should trigger the delete modal.
 *
 * @param code - The keyboard code
 * @returns True if delete modal should be shown
 *
 * @example
 * ```tsx
 * if (isKeyboardDeleteAction('KeyR')) {
 *   // Show delete modal
 * }
 * ```
 */
export function isKeyboardDeleteAction(code: string): boolean {
  return shouldShowDeleteModal(code);
}
